import React, { useEffect, useRef, useState } from "react";
import { RCCAlert, RCCHookToast, RCCServicesApi } from "react-common-components";
import { useTranslation } from "src/context/Translations/context";
import { useLocation, useParams } from "react-router-dom";
import getUrlSearchParams from "src/utils/getUrlSearchParams";
import formatDate from "src/utils/formatDate";
import { standardErrorMessages } from "src/utils/errors";
import Loader from "src/components/Loader";
import { IConfigsIncomeStatement } from "./interface";
import IncomeStatement from "./incomeStatement";

const IncomeStatementPage: React.FC = () => {
	const params = useParams<{
        budgetPlanId: string;
    }>();
	
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	
	const query = useLocation();
	const objQueryParams = getUrlSearchParams(query.search);

	const [financialPlanTitle] = useState(objQueryParams?.name || "");
	
	const configs = useRef<IConfigsIncomeStatement>({ 
		showPeriods: "T",
		variation: "PR",
		divider: "1",
		updateIndicators: "S",
		corporativeValues: "N",
		forecast: "N",
		showColumnLastYear: "N",
		showColumnPlanned: "S",
		showColumnRealized: "S",
		showVariationValue: "N",
		showVariationPercent: "N",
		showHorizontalAnalysis: "N",
		showQuantity: "N",
		showVerticalAnalysis: "S",
		startDate: new Date(new Date(new Date().getFullYear(), 0, 1)),
		endDate: new Date(new Date(new Date().getFullYear(), 11, 31)),
		dtForecast: formatDate(new Date(new Date(new Date().getFullYear(), 0, 1))),
		coinPlanned: null,
		coinRealized: null,
		coinPlannedInitials: null,
		coinRealizedInitials: null
	});
	const subsidiaryDefaultValue = useRef<string | undefined>(undefined);
	const subsidiaryDefaultLabel = useRef<string | undefined>(undefined);
	const [errorLoading, setErrorLoading] = useState(false);
	const [loading, setLoading] = useState(true);

	const loadData = async () => {
		if(!params.budgetPlanId)
			return;
			
		try{
			setLoading(true);
			setErrorLoading(false);

			const {data} = await RCCServicesApi.getRequest({
				method: "FinancialManagement/IncomeStatementConfigs",
				urlParameters: {
					budgetPlanId: params.budgetPlanId
				}
			});
			if(data && data !== null){
				configs.current = {
					corporativeValues: data.ofm007Valorescorp,
					divider: data.ofm007Dividendo !== null ? String(data.ofm007Dividendo) : "1",
					dtForecast: data.ofm007Dtforecast !== null ? formatDate(data.ofm007Dtforecast) : formatDate(new Date(new Date(new Date().getFullYear(), 0, 1))),
					endDate: data.ofm007Datafim !== null ? data.ofm007Datafim : new Date(new Date(new Date().getFullYear(), 11, 31)),
					forecast: data.ofm007Forecast,
					showColumnLastYear: data.ofm007Colanoanterior,
					showColumnPlanned: data.ofm007Colplanejado,
					showColumnRealized: data.ofm007Colrealizado,
					showHorizontalAnalysis: data.ofm007Colanaliseh,
					showPeriods: data.ofm007Mostrarperiodos === 1 ? "T" : "A",
					showQuantity: data.ofm007Colvalorqtde,
					showVariationPercent: data.ofm007Colvarperc,
					showVariationValue: data.ofm007Colvarvalor,
					showVerticalAnalysis: data.ofm007Colanalisev,
					startDate: data.ofm007Dataini !== null ? data.ofm007Dataini : new Date(new Date(new Date().getFullYear(), 11, 31)),
					updateIndicators: data.ofm007Atuindicadores,
					variation: data.ofm007Variacao === 1 ? "PR" : "RL",
					coinPlanned: data.ofm007Moa001Id,
					coinRealized: data.ofm007Moa001Id2,
					coinPlannedInitials: data.coinPlannedInitials,
					coinRealizedInitials: data.coinRealizedInitials
				};
				subsidiaryDefaultValue.current = data.ofm007Ofa002Id !== null ? data.ofm007Ofa002Id.toString() : null;
				subsidiaryDefaultLabel.current = data.ofm007Ofa002Nome !== null ? data.ofm007Ofa002Nome.toString() : undefined;
			}
			setLoading(false);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao carregar os dados"), addToast);
			setLoading(false);
			setErrorLoading(true);
		}
	};

	useEffect(() => {
		loadData();
	}, []);

	if(!params.budgetPlanId)
		return <RCCAlert 
			title={translate("Atenção")}
			description={translate("Nenhum plano orçamentário selecionado")}
		/>;

	if(loading)
		return <Loader />;


	if(errorLoading)
		return <RCCAlert 
			title={translate("Atenção")}
			description={translate("Não foram encontrados dados")}
		/>;

	return (
		<IncomeStatement 
			budgetPlanId={params.budgetPlanId}
			budgetPlanTitle={financialPlanTitle}
			configs={configs.current}
			subsidiaryDefaultValue={subsidiaryDefaultValue.current}
			subsidiaryDefaultLabel={subsidiaryDefaultLabel.current}
		/>
	);
};

export default IncomeStatementPage;
